/**
 * Search Results Enhancement with AI
 * Enhances the search results page with AI-powered product matching
 */

class SearchResultsEnhancer {
  constructor() {
    this.init();
  }

  async init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.enhance());
    } else {
      this.enhance();
    }
  }

  async enhance() {
    const searchResults = document.querySelector("[data-search-results]");
    if (!searchResults) return;

    const query = this.getSearchQuery();
    if (!query) return;

    const hasNativeResults = this.hasNativeResults(searchResults);

    if (window.productAISearch) {
      try {
        const aiResults = await window.productAISearch.searchProducts(query, {
          limit: 12,
          timeout:
            window.getAISearchConfig?.("search.searchPageTimeout", 1200) ||
            1200,
          retries: 0,
        });

        if (aiResults?.results && aiResults.results.length > 0) {
          const aiSection = this.createAISuggestionsSection(
            aiResults.results,
            query
          );

          if (aiSection) {
            this.insertBeforeNativeResults(searchResults, aiSection);
          }
        }
      } catch (error) {
        console.warn("Failed to enhance search results with AI:", error);
        // Keep Shopify results and the static recovery path available.
      }
    }

    if (!hasNativeResults) {
      const recoverySection = this.createNoResultsRecoverySection(query);
      this.insertBeforeNativeResults(searchResults, recoverySection);
    }
  }

  hasNativeResults(searchResults) {
    return Boolean(
      searchResults.querySelector(
        ".product-grid > li, .product-grid .grid__item"
      )
    );
  }

  insertBeforeNativeResults(searchResults, section) {
    if (!section) return;

    const existingResults = searchResults.querySelector(".product-grid");
    if (existingResults?.parentElement) {
      existingResults.parentElement.insertBefore(section, existingResults);
    } else {
      searchResults.insertBefore(section, searchResults.firstChild);
    }
  }

  /**
   * Create AI suggestions section
   */
  createAISuggestionsSection(results, query) {
    const section = document.createElement("section");
    section.className = "search-results__ai-section";
    section.setAttribute("aria-label", "Résultats de recherche améliorés");

    const heading = document.createElement("h2");
    heading.className = "search-results__ai-heading";
    heading.textContent = 'Meilleurs résultats pour "' + query + '"';

    section.appendChild(heading);

    const groups = this.groupResults(results);
    const groupLabels = {
      product: "Produits",
      collection: "Collections",
      article: "Guides et articles",
      page: "Pages",
    };

    ["product", "collection", "article", "page"].forEach((type) => {
      const items = groups[type];
      if (!items.length) return;
      const cards = items
        .map((result) => this.createResultCard(result))
        .filter(Boolean);
      if (!cards.length) return;

      const group = document.createElement("div");
      group.className = "search-results__ai-group";

      const groupHeading = document.createElement("h3");
      groupHeading.className = "search-results__ai-group-heading";
      groupHeading.textContent = groupLabels[type];

      const grid = document.createElement("div");
      grid.className = `search-results__ai-grid search-results__ai-grid--${type}`;

      cards.forEach((card) => grid.appendChild(card));

      group.appendChild(groupHeading);
      group.appendChild(grid);
      section.appendChild(group);
    });

    return section.querySelector(".search-results__ai-group") ? section : null;
  }

  groupResults(results) {
    return {
      product: results.filter((result) => result.type === "product"),
      collection: results.filter((result) => result.type === "collection"),
      article: results.filter((result) => result.type === "article"),
      page: results.filter((result) => result.type === "page"),
    };
  }

  /**
   * Create a product card element
   */
  createResultCard(result) {
    const safeUrl = this.getSafeResultUrl(result);
    if (!safeUrl) return null;

    const article = document.createElement("article");
    article.className = "search-results__ai-card";

    const link = document.createElement("a");
    link.href = safeUrl;
    link.className = "search-results__ai-card-link";

    // Image
    const imageWrapper = document.createElement("div");
    imageWrapper.className = "search-results__ai-card-image";

    if (result.image) {
      const img = document.createElement("img");
      img.src = result.image;
      img.alt = result.title || result.name || "";
      img.loading = "lazy";
      imageWrapper.appendChild(img);
    } else {
      imageWrapper.style.backgroundColor = "#f0f0f0";
    }

    // Content
    const content = document.createElement("div");
    content.className = "search-results__ai-card-content";

    const title = document.createElement("h3");
    title.className = "search-results__ai-card-title";
    title.textContent = result.title || result.name || "Résultat";

    if (result.vendor || result.excerpt) {
      const meta = document.createElement("p");
      meta.className = "search-results__ai-card-vendor";
      meta.textContent = result.vendor || result.excerpt;
      content.appendChild(meta);
    }

    if (result.price) {
      const price = document.createElement("p");
      price.className = "search-results__ai-card-price";
      price.textContent = `${Number(result.price).toFixed(2)}€`;
      content.appendChild(price);
    }

    content.appendChild(title);

    const badge = document.createElement("span");
    badge.className = "search-results__ai-badge";
    badge.textContent = this.getTypeLabel(result.type);
    content.appendChild(badge);

    link.appendChild(imageWrapper);
    link.appendChild(content);
    article.appendChild(link);

    return article;
  }

  getSafeResultUrl(result) {
    return window.productAISearch?.getSafeResultUrl?.(result) || null;
  }

  createNoResultsRecoverySection(query) {
    const section = document.createElement("section");
    section.className =
      "search-results__ai-section search-results__recovery-section";
    section.setAttribute("aria-label", "Continuer après une recherche sans résultat");

    const heading = document.createElement("h2");
    heading.className = "search-results__ai-heading";
    heading.textContent = "Poursuivez votre recherche";
    section.appendChild(heading);

    const explanation = document.createElement("p");
    explanation.textContent = `Aucun produit ne correspond exactement à « ${query} ». Vérifiez l’orthographe, essayez une marque ou une référence plus courte, ou explorez ces accès directs.`;
    section.appendChild(explanation);

    this.getNoResultsRecoveryGroups().forEach((recoveryGroup) => {
      const group = document.createElement("div");
      group.className = "search-results__ai-group";

      const groupHeading = document.createElement("h3");
      groupHeading.className = "search-results__ai-group-heading";
      groupHeading.textContent = recoveryGroup.heading;
      group.appendChild(groupHeading);

      const list = document.createElement("ul");
      list.className =
        "search-results__ai-grid search-results__recovery-grid list-unstyled";

      recoveryGroup.items.forEach((item) => {
        const listItem = document.createElement("li");
        listItem.className = "search-results__ai-card";

        const link = document.createElement("a");
        link.className = "search-results__ai-card-link";
        link.href = item.url;

        const content = document.createElement("span");
        content.className = "search-results__ai-card-content";

        const label = document.createElement("span");
        label.className = "search-results__ai-card-title";
        label.textContent = item.label;

        content.appendChild(label);
        link.appendChild(content);
        listItem.appendChild(link);
        list.appendChild(listItem);
      });

      group.appendChild(list);
      section.appendChild(group);
    });

    return section;
  }

  getNoResultsRecoveryGroups() {
    const searchTerms = [
      "Télescope débutant",
      "Oculaire 1.25",
      "Caméra planétaire",
    ];

    return [
      {
        heading: "Catégories populaires",
        items: [
          { label: "Télescopes", url: "/collections/telescopes" },
          { label: "Oculaires", url: "/collections/oculaires" },
          { label: "Montures", url: "/collections/montures" },
        ],
      },
      {
        heading: "Recherches fréquentes",
        items: searchTerms.map((term) => ({
          label: term,
          url: `/search?q=${encodeURIComponent(
            term
          )}&options%5Bprefix%5D=last`,
        })),
      },
      {
        heading: "Besoin d’aide ?",
        items: [
          {
            label: "Voir les meilleures ventes",
            url: "/collections/all?sort_by=best-selling",
          },
          { label: "Demander conseil", url: "/pages/contact" },
        ],
      },
    ];
  }

  getTypeLabel(type) {
    if (type === "product") return "Produit";
    if (type === "collection") return "Collection";
    if (type === "article") return "Article";
    return "Page";
  }

  /**
   * Get search query from URL or page
   */
  getSearchQuery() {
    // Try to get from URL params
    const params = new URLSearchParams(window.location.search);
    const query = params.get("q");
    if (query) return query;

    // Try to get from page heading
    const heading = document.querySelector("[data-search-query]");
    if (heading) return heading.textContent.trim();

    return null;
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }
}

// Initialize when page loads
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => new SearchResultsEnhancer()
  );
} else {
  new SearchResultsEnhancer();
}
